import { Questionnaire } from "@medplum/fhirtypes";

import { ZodObject, ZodRawShape } from "zod";

import {
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_FLOWSHEET_VALIDATION_ERROR_MESSAGE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { normalizeQuestionnaireDateTimeGroupsToUtcParts } from "@/encounters/components/EncounterLabor/utils/form-datetime";
import { toast } from "@/utils/notifications";

/** The slice of a Mantine form this helper needs. */
interface ValidatableQuestionnaireForm {
  values: unknown;
  validate: () => { hasErrors: boolean };
}

interface BuildLaborQuestionnaireSubmitValuesInput {
  form: ValidatableQuestionnaireForm;
  formSchema: ZodObject<ZodRawShape>;
  questionnaire: Questionnaire;
  /** FHIR valueDate anchors per DateTime group. Quick-add has none. */
  dateTimeUtcDateAnchors?: Record<string, string>;
}

/**
 * Validates a questionnaire-driven labor form, then parses and UTC-normalizes its values.
 *
 * Returns null when the form has field errors — `validate` has already flagged them inline (a
 * cleared Sign Initials, say), and a toast tells the caller's user to look there. Validating here
 * rather than at each call site keeps errors on the fields instead of surfacing as a parse
 * failure. Shared by the three drawers that predate `useLaborFlowsheetDrawerPersist`.
 */
export function buildLaborQuestionnaireSubmitValues({
  form,
  formSchema,
  questionnaire,
  dateTimeUtcDateAnchors,
}: BuildLaborQuestionnaireSubmitValuesInput): Record<string, unknown> | null {
  if (form.validate().hasErrors) {
    toast({
      type: "error",
      title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
      message: LABOR_FLOWSHEET_VALIDATION_ERROR_MESSAGE,
      autoClose: false,
    });
    return null;
  }

  const parsedValues = formSchema.parse(form.values) as Record<string, unknown>;

  return normalizeQuestionnaireDateTimeGroupsToUtcParts(
    questionnaire,
    parsedValues,
    dateTimeUtcDateAnchors,
  );
}
